import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// eslint-disable-next-line import/order
import { ExaminerService } from '../services/llmClient/ExaminerService';
import eventStreamService from '../services/supabaseClient/eventStreamService';
// ⏳ Async thunk to fetch questions from the API
import { setActivityContent } from './classroomSlice';

export const fetchExamQuestions = createAsyncThunk(
  'exam/fetchExamQuestions',
  async (
    { lessonTitle, studentProfile, courseDetails, questionCount = 10 },
    { dispatch, rejectWithValue }
  ) => {
    try {
      void eventStreamService.emitEvent({
        userId: studentProfile?.id || null,
        sessionId: `exam:${lessonTitle}`,
        streamKey: `exam:${lessonTitle}`,
        eventFamily: 'exam',
        eventType: 'exam_generation_requested',
        eventName: 'exam_generation_requested',
        origin: 'exam_slice',
        actorType: 'user',
        actorId: studentProfile?.id || null,
        entityType: 'lesson_exam',
        entityId: lessonTitle,
        courseId: courseDetails?.id || null,
        payload: {
          lessonTitle,
          questionCount: Math.max(10, Number(questionCount) || 10),
          courseId: courseDetails?.id || null,
          courseTitle: courseDetails?.title || null,
        },
      });

      const examPayload = await ExaminerService.generateStudentExam({
        lessonTitle,
        studentProfile,
        courseDetails,
        questionCount: Math.max(10, Number(questionCount) || 10),
      });

      const questions = examPayload.questions.map((question) => ({
        question_id: question.id,
        question_text: question.prompt,
        question_type: question.type,
        field_type:
          question.type === 'single_choice'
            ? 'radio'
            : question.type === 'multi_select'
              ? 'checkbox'
              : question.type === 'long_text' || question.type === 'code'
                ? 'textarea'
                : question.type === 'numeric'
                  ? 'number'
                  : 'text',
        points: question.points,
        difficulty_level: question.difficulty || 'medium',
        required: question.required ?? true,
        options: question.options || [],
        ui: question.ui || {},
        metadata: question.metadata || {},
      }));

      if (!questions || questions.length === 0) {
        throw new Error('No questions returned.');
      }

      // Also update activity.content in classroomSlice
      dispatch(setActivityContent({ content: questions }));

      void eventStreamService.emitEvent({
        userId: studentProfile?.id || null,
        sessionId: examPayload.sessionId || `exam:${lessonTitle}`,
        streamKey: `exam:${examPayload.examId || lessonTitle}`,
        eventFamily: 'exam',
        eventType: 'exam_generation_succeeded',
        eventName: 'exam_generation_succeeded',
        origin: 'exam_slice',
        actorType: 'system',
        actorId: 'examiner_service',
        entityType: 'exam',
        entityId: examPayload.examId || lessonTitle,
        examId: examPayload.examId || null,
        courseId: examPayload.courseId || courseDetails?.id || null,
        success: true,
        payload: {
          lessonTitle,
          questionCount: questions.length,
          totalPoints: examPayload.totalPoints || questions.reduce((sum, q) => sum + q.points, 0),
          courseId: examPayload.courseId || courseDetails?.id || null,
          courseTitle: examPayload.courseTitle || courseDetails?.title || null,
        },
      });

      return {
        examId: examPayload.examId || `exam_${Date.now()}`,
        sessionId: examPayload.sessionId || null,
        courseId: examPayload.courseId || courseDetails?.id || null,
        courseTitle: examPayload.courseTitle || courseDetails?.title || null,
        title: examPayload.title || lessonTitle,
        instructions: examPayload.instructions || '',
        totalPoints: examPayload.totalPoints || questions.reduce((sum, q) => sum + q.points, 0),
        questions,
      };
    } catch (error) {
      void eventStreamService.emitEvent({
        userId: studentProfile?.id || null,
        sessionId: `exam:${lessonTitle}`,
        streamKey: `exam:${lessonTitle}`,
        eventFamily: 'exam',
        eventType: 'exam_generation_failed',
        eventName: 'exam_generation_failed',
        origin: 'exam_slice',
        actorType: 'system',
        actorId: 'examiner_service',
        entityType: 'lesson_exam',
        entityId: lessonTitle,
        courseId: courseDetails?.id || null,
        success: false,
        errorCode: error?.message || 'exam_generation_failed',
        payload: {
          lessonTitle,
          courseId: courseDetails?.id || null,
          courseTitle: courseDetails?.title || null,
        },
      });
      return rejectWithValue(error.message || 'Failed to fetch exam questions.');
    }
  }
);

export const submitExamSession = createAsyncThunk(
  'exam/submitExamSession',
  async ({ userId }, { getState, rejectWithValue }) => {
    try {
      const state = getState().exam;
      const sessionId = state.exam.sessionId;
      const examId = state.exam.examId;
      const courseId = state.exam.courseId;
      const courseTitle = state.exam.courseTitle;
      const answers = state.studentAnswers || {};

      if (!sessionId || !examId) {
        throw new Error('Exam session is not initialized.');
      }
      const result = await ExaminerService.submitStudentSession({
        sessionId,
        examId,
        userId: userId || null,
        answers,
      });

      void eventStreamService.emitEvent({
        userId: userId || null,
        sessionId,
        streamKey: `exam:${examId}`,
        eventFamily: 'exam',
        eventType: 'exam_submission_succeeded',
        eventName: 'exam_submission_succeeded',
        origin: 'exam_slice',
        actorType: 'user',
        actorId: userId || null,
        entityType: 'exam',
        entityId: examId,
        examId,
        courseId: courseId || null,
        success: true,
        scoreRaw: result?.scoreRaw ?? result?.score_raw ?? null,
        scoreMax: result?.scoreMax ?? result?.score_max ?? null,
        scorePct: result?.scorePct ?? result?.score_pct ?? null,
        payload: {
          answerCount: Object.keys(answers || {}).length,
          courseId: courseId || null,
          courseTitle: courseTitle || null,
        },
      });

      return result;
    } catch (error) {
      const state = getState().exam;
      void eventStreamService.emitEvent({
        userId: userId || null,
        sessionId: state?.exam?.sessionId || null,
        streamKey: `exam:${state?.exam?.examId || 'unknown'}`,
        eventFamily: 'exam',
        eventType: 'exam_submission_failed',
        eventName: 'exam_submission_failed',
        origin: 'exam_slice',
        actorType: 'user',
        actorId: userId || null,
        entityType: 'exam',
        entityId: state?.exam?.examId || 'unknown',
        examId: state?.exam?.examId || null,
        courseId: state?.exam?.courseId || null,
        success: false,
        errorCode: error?.message || 'exam_submission_failed',
        payload: {
          courseId: state?.exam?.courseId || null,
          courseTitle: state?.exam?.courseTitle || null,
        },
      });
      return rejectWithValue(error.message || 'Failed to submit exam.');
    }
  }
);

// 🚀 Exam Slice
// Utility to load exam state from localStorage
const loadExamState = () => {
  const defaultState = {
    exam: {
      examId: null,
      sessionId: null,
      courseId: null,
      courseTitle: null,
      title: null,
      instructions: '',
      totalPoints: 0,
      isExamActive: false,
      isExamSubmitted: false,
    },
    questions: [],
    studentAnswers: {},
    currentQuestionIndex: 0,
    lastSavedAt: null,
    result: null,
    loading: false,
    error: null,
  };

  try {
    const stored = localStorage.getItem('examState');
    if (!stored) return defaultState;

    const parsed = JSON.parse(stored);
    return {
      ...defaultState,
      ...parsed,
      exam: {
        ...defaultState.exam,
        ...(parsed?.exam || {}),
      },
    };
  } catch {
    return defaultState;
  }
};

const examSlice = createSlice({
  name: 'exam',
  initialState: loadExamState(),
  reducers: {
    startExam: (state) => {
      state.exam.isExamActive = true;
      state.exam.isExamSubmitted = false;
      state.studentAnswers = {};
      state.currentQuestionIndex = 0;
      localStorage.setItem('examState', JSON.stringify(state));
    },
    submitAnswer(state, action) {
      const { questionId, answer } = action.payload;
      state.studentAnswers[questionId] = answer;
      state.lastSavedAt = new Date().toISOString();
      localStorage.setItem('examState', JSON.stringify(state));
    },
    submitExam(state) {
      state.exam.isExamActive = false;
      state.exam.isExamSubmitted = true;
      localStorage.setItem('examState', JSON.stringify(state));
    },
    nextQuestion(state) {
      state.currentQuestionIndex = Math.min(
        state.currentQuestionIndex + 1,
        state.questions.length - 1
      );
      localStorage.setItem('examState', JSON.stringify(state));
    },
    previousQuestion(state) {
      state.currentQuestionIndex = Math.max(state.currentQuestionIndex - 1, 0);
      localStorage.setItem('examState', JSON.stringify(state));
    },
    setQuestionIndex(state, action) {
      const nextIndex = Number(action.payload);
      if (Number.isNaN(nextIndex)) return;
      state.currentQuestionIndex = Math.max(0, Math.min(nextIndex, state.questions.length - 1));
      localStorage.setItem('examState', JSON.stringify(state));
    },
    resetExam(state) {
      state.exam.isExamActive = false;
      state.exam.isExamSubmitted = false;
      state.studentAnswers = {};
      state.currentQuestionIndex = 0;
      state.questions = [];
      state.exam.examId = null;
      state.exam.sessionId = null;
      state.exam.courseId = null;
      state.exam.courseTitle = null;
      state.exam.title = null;
      state.exam.instructions = '';
      state.exam.totalPoints = 0;
      state.result = null;
      state.lastSavedAt = null;
      localStorage.removeItem('examState');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExamQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExamQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.exam.examId = action.payload.examId;
        state.exam.sessionId = action.payload.sessionId;
        state.exam.courseId = action.payload.courseId || null;
        state.exam.courseTitle = action.payload.courseTitle || null;
        state.exam.title = action.payload.title;
        state.exam.instructions = action.payload.instructions;
        state.exam.totalPoints = action.payload.totalPoints;
        state.questions = action.payload.questions;
        // Also update activity.content in classroomSlice
        // This must be dispatched from the thunk or component, not here in the reducer
      })
      .addCase(fetchExamQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch exam.';
      })
      .addCase(submitExamSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitExamSession.fulfilled, (state, action) => {
        state.loading = false;
        state.exam.isExamActive = false;
        state.exam.isExamSubmitted = true;
        state.result = action.payload;
        localStorage.setItem('examState', JSON.stringify(state));
      })
      .addCase(submitExamSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to submit exam.';
      });
  },
});

// ✅ Export Actions
export const {
  startExam,
  submitAnswer,
  submitExam,
  nextQuestion,
  previousQuestion,
  setQuestionIndex,
  resetExam,
} = examSlice.actions;

// ✅ Selectors
export const selectExam = (state) => state.exam.exam;
export const selectQuestions = (state) => state.exam.questions;
export const selectCurrentQuestionIndex = (state) => state.exam.currentQuestionIndex;
export const selectStudentAnswers = (state) => state.exam.studentAnswers;
export const selectExamLoading = (state) => state.exam.loading;
export const selectExamError = (state) => state.exam.error;
export const selectExamState = (state) => state.exam;

// ✅ Export Reducer
export default examSlice.reducer;
